/**
 * The post-signup intent question, shared by the prompt (src/components/
 * onboarding/IntentPrompt.tsx) and its endpoint (api/me/intent.ts), plus the
 * signup attribution fields added to `signup_completed`.
 */

import type { Persona } from '../email-engine/types.js';

export type IntentAnswer = 'visit' | 'run_show' | 'find_sequences' | 'make_sequences' | 'install';
export type IntentArm = 'asked' | 'control';

export const INTENT_OPTIONS: { value: IntentAnswer; label: string; hint: string }[] = [
  { value: 'visit', label: 'Visit light shows near me', hint: 'Find shows on the map and plan a route.' },
  { value: 'run_show', label: 'I run a light show', hint: 'List your display so visitors can find it.' },
  { value: 'find_sequences', label: 'Find sequences for my show', hint: 'Browse and download sequences for your lights.' },
  { value: 'make_sequences', label: 'I make sequences', hint: 'Sell or share the sequences you make.' },
  { value: 'install', label: 'I install lights for clients', hint: 'You put up displays for homes or businesses.' },
];

const ANSWER_MAP: Record<IntentAnswer, { persona: Persona; flag?: string }> = {
  visit: { persona: 'viewer' },
  run_show: { persona: 'show_owner' },
  find_sequences: { persona: 'show_owner', flag: 'beginner' },
  make_sequences: { persona: 'sequencer' },
  install: { persona: 'installer' },
};

export function isIntentAnswer(value: unknown): value is IntentAnswer {
  return INTENT_OPTIONS.some((option) => option.value === value);
}

export function answersToDeclared(answers: IntentAnswer[]): { declared: Persona[]; flags: string[] } {
  const declared = new Set<Persona>();
  const flags = new Set<string>();
  for (const answer of answers) {
    const { persona, flag } = ANSWER_MAP[answer];
    declared.add(persona);
    if (flag) flags.add(flag);
  }
  return { declared: [...declared], flags: [...flags] };
}

export type OwnerFocus = 'show' | 'sequences' | 'both';

export const OWNER_FOCUS_VALUES: readonly OwnerFocus[] = ['show', 'sequences', 'both'];

/** What the intent answers say about the owner's role. Only the two role answers count; null means they said nothing. */
export function focusFromAnswers(answers: IntentAnswer[]): OwnerFocus | null {
  const show = answers.includes('run_show');
  const sequences = answers.includes('make_sequences');
  if (show && sequences) return 'both';
  if (show) return 'show';
  if (sequences) return 'sequences';
  return null;
}

/** The question is only for accounts younger than this. */
export const INTENT_MAX_ACCOUNT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function isNewAccount(createdAt: string | null | undefined, now = Date.now()): boolean {
  const created = createdAt ? Date.parse(createdAt) : NaN;
  return Number.isFinite(created) && now - created < INTENT_MAX_ACCOUNT_AGE_MS;
}

/** Stable 50/50 split: parity of the summed char codes of the user id. */
export function intentArm(userId: string): IntentArm {
  let sum = 0;
  for (let i = 0; i < userId.length; i++) sum += userId.charCodeAt(i);
  return sum % 2 === 0 ? 'asked' : 'control';
}

export type ReferrerClass = 'facebook' | 'xlights_forum' | 'reddit' | 'remotefalcon' | 'youtube' | 'google' | 'direct' | 'other';

/** Classify document.referrer by host. Our own host counts as direct. */
export function referrerClass(referrer: string, ownHost: string): ReferrerClass {
  if (!referrer) return 'direct';
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return 'other';
  }
  const is = (domain: string) => host === domain || host.endsWith(`.${domain}`);
  const own = ownHost.toLowerCase().replace(/^www\./, '').replace(/^studio\./, '');
  if (is(own)) return 'direct';
  if (is('facebook.com') || is('fb.com') || is('fb.me')) return 'facebook';
  if (is('xlights.org')) return 'xlights_forum';
  if (is('reddit.com') || is('redd.it')) return 'reddit';
  if (is('remotefalcon.com')) return 'remotefalcon';
  if (is('youtube.com') || is('youtu.be')) return 'youtube';
  // Search only: google.<tld> (www. already stripped); mail., docs. etc. are other.
  if (/^google\.[a-z]{2,3}(\.[a-z]{2})?$/.test(host)) return 'google';
  return 'other';
}

const LANDING_KEY = 'lh-landing';

/** Call once on app load: keeps the first path and referrer class of this
 *  session. Path only, never the query string. */
export function rememberLanding(): void {
  try {
    if (sessionStorage.getItem(LANDING_KEY)) return;
    sessionStorage.setItem(
      LANDING_KEY,
      JSON.stringify({
        landing_path: window.location.pathname,
        referrer_class: referrerClass(document.referrer, window.location.hostname),
      }),
    );
  } catch {
    // Storage blocked: signup_completed just goes without these fields.
  }
}

export function landingContext(): { landing_path?: string; referrer_class?: ReferrerClass } {
  try {
    return JSON.parse(sessionStorage.getItem(LANDING_KEY) ?? '{}');
  } catch {
    return {};
  }
}
